import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { QuickNewCustomerDialog, QuickNewPetDialog } from "./Boarding";
import CustomerSearchPicker from "../components/CustomerSearchPicker";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  in_progress: "bg-purple-100 text-purple-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [services, setServices] = useState([]);
  const [groomingRates, setGroomingRates] = useState([]);
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [f, setF] = useState({
    pet_id: "",
    kind: "grooming",
    service_id: "", grooming_rate_id: "",
    slot_date: date, slot_time: "10:00", notes: "",
  });
  const [pets, setPets] = useState([]);
  const [newCustOpen, setNewCustOpen] = useState(false);
  const [newPetOpen, setNewPetOpen] = useState(false);

  const load = () => api.get("/appointments", { params: { date } }).then(r => setItems(r.data));
  useEffect(() => { load(); }, [date]); // eslint-disable-line
  useEffect(() => {
    api.get("/services").then(r => setServices(r.data));
    api.get("/grooming/rates").then(r => setGroomingRates(r.data.filter(g => g.is_active !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    if (customer?.id) api.get("/pets", { params: { customer_id: customer.id } }).then(r => setPets(r.data));
    else setPets([]);
  }, [customer]);

  const save = async () => {
    if (!customer?.id || !f.pet_id) { toast.error("Customer and pet required"); return; }
    if (f.kind === "grooming" && !f.grooming_rate_id) { toast.error("Pick a grooming service"); return; }
    if (f.kind === "service" && !f.service_id) { toast.error("Pick a service"); return; }
    try {
      const slot_start = `${f.slot_date}T${f.slot_time}:00`;
      const payload = {
        customer_id: customer.id, pet_id: f.pet_id,
        kind: f.kind,
        service_id: f.kind === "service" ? f.service_id : null,
        grooming_rate_id: f.kind === "grooming" ? f.grooming_rate_id : null,
        slot_start, notes: f.notes,
      };
      await api.post("/appointments", payload);
      toast.success("Appointment booked");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}`, { status }); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="appointments-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Schedule</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Appointments</h1>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="appt-date-filter" />
          <Button onClick={() => { setF(p => ({ ...p, slot_date: date })); setOpen(true); }} data-testid="new-appt-btn"><Plus className="w-4 h-4 mr-1" />New booking</Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="divide-y divide-border">
          {items.map(a => (
            <div key={a.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`appt-${a.id}`}>
              <div className="w-20 font-mono font-bold">{new Date(a.slot_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              <div className="flex-1 min-w-[160px]">
                <div className="font-semibold text-sm">{a.customer?.name || "—"} · {a.pet?.name || "—"} ({a.pet?.species})</div>
                <div className="text-xs text-muted-foreground">
                  <Badge variant="outline" className="text-[10px] mr-1">{a.kind || "service"}</Badge>
                  {a.service?.name || "—"}
                </div>
              </div>
              <Badge className={STATUS_COLORS[a.status] || ""}>{a.status}</Badge>
              <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-muted-foreground py-6">No appointments on this day.</div>}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="appt-dialog">
          <DialogHeader><DialogTitle>New booking</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div>
              <Label>Customer</Label>
              <CustomerSearchPicker
                value={customer}
                onChange={(c) => { setCustomer(c); setF(p => ({ ...p, pet_id: "" })); }}
                onClear={() => { setCustomer(null); setF(p => ({ ...p, pet_id: "" })); }}
                onNewClick={() => setNewCustOpen(true)}
                placeholder="Search by name or mobile number…"
                testIdPrefix="appt-cust"
                autoFocus
              />
            </div>
            <div>
              <Label>Pet</Label>
              <div className="flex gap-1">
                <Select value={f.pet_id} onValueChange={v => setF(p => ({ ...p, pet_id: v }))} disabled={!customer?.id}>
                  <SelectTrigger data-testid="appt-pet"><SelectValue placeholder={customer?.id ? "Select pet…" : "Pick customer first"} /></SelectTrigger>
                  <SelectContent>{pets.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.species})</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="icon" type="button" onClick={() => setNewPetOpen(true)} disabled={!customer?.id} data-testid="appt-new-pet"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={f.kind} onValueChange={v => setF(p => ({ ...p, kind: v, service_id: "", grooming_rate_id: "" }))}>
                <SelectTrigger data-testid="appt-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="grooming">Grooming</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="boarding">Boarding (visit)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {f.kind === "grooming" && (
              <div>
                <Label>Grooming service</Label>
                <Select value={f.grooming_rate_id} onValueChange={v => setF(p => ({ ...p, grooming_rate_id: v }))}>
                  <SelectTrigger data-testid="appt-grooming"><SelectValue placeholder="Pick from grooming catalog…" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {groomingRates.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">No grooming rates set up. <a href="/grooming" className="text-primary underline">Configure →</a></div>}
                    {groomingRates.map(g => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.species} · {g.label} · ₹{g.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {f.kind === "service" && (
              <div>
                <Label>Service</Label>
                <Select value={f.service_id} onValueChange={v => setF(p => ({ ...p, service_id: v }))}>
                  <SelectTrigger data-testid="appt-service"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · ₹{s.price}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {f.kind === "boarding" && (
              <div className="text-xs text-muted-foreground bg-muted rounded-md p-3">
                For multi-day stays with rooms, belongings and check-in/out, use <a href="/boarding" className="text-primary underline">Boarding → New reservation</a>. This option here just blocks a slot on the calendar.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Date</Label><Input type="date" value={f.slot_date} onChange={e => setF(p => ({ ...p, slot_date: e.target.value }))} data-testid="appt-date" /></div>
              <div><Label>Time</Label><Input type="time" value={f.slot_time} onChange={e => setF(p => ({ ...p, slot_time: e.target.value }))} data-testid="appt-time" /></div>
            </div>
            <div><Label>Notes</Label><Input value={f.notes} onChange={e => setF(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-appt">Book & notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickNewCustomerDialog
        open={newCustOpen} onOpenChange={setNewCustOpen}
        onCreated={(c) => { setCustomer(c); setF(p => ({ ...p, pet_id: "" })); setNewCustOpen(false); }}
      />
      <QuickNewPetDialog
        open={newPetOpen} onOpenChange={setNewPetOpen} customerId={customer?.id}
        onCreated={(p) => { setPets(prev => [p, ...prev]); setF(prev => ({ ...prev, pet_id: p.id })); setNewPetOpen(false); }}
      />
    </div>
  );
}
